import React from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import {Stack, Grid, Typography, CardContent, Tooltip, Button} from '@mui/material';
import {SmartLink, formatDurationAbbr, formatRFC7231} from '../abelcommon';
import {useBlock, useSummary} from '../queries';
import {QueryProcessor} from '../components/QueryProcessor';
import {TabbedMainCard} from '../components/MainCard';
import {JSONTextField} from '../components/JSONTextField';
import {RichList, RichListItem} from '../components/RichList';
import BlockTxList from "../components/BlockTxList";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {useRecoilValue} from "recoil";
import {latestBlockState} from "../state";

const useBlockWithPrecheck = (block_height_or_hash?: string | number) => {
    let k: number | string = block_height_or_hash ?? '';
    if (typeof k === 'string' && !k.startsWith('0x')) {
        k = Number(k);
        if (isNaN(k)) {
            throw new Error('Illegal block height or hash.');
        }
    }
    return useBlock(k);
};

const BlockOverview = ({d}: { d: any }) => {
    const summaryQuery = useSummary();
    const latestBlockHeight = summaryQuery.isSuccess ? summaryQuery.data.payload?.latestBlockHeight : null;
    const treatAsLatest = !latestBlockHeight || d.height >= latestBlockHeight;

    const ts = new Date();
    const dts = d.time * 1000;
    return (
        <RichList sx={{width: 1, mt: -2}}>
            <RichListItem
                title={'Block Height'}
                help={
                    'Also known as Block Number, indicates the position of a particular block in the blockchain'
                }
            >
                <Stack direction={'row'} sx={{alignItems: 'center'}} spacing={0}>
                    <Typography sx={{pr: 1, fontWeight: {xs: 'normal', sm: 'bold'}}}>{d.height}</Typography>
                    {!treatAsLatest &&
                        <Typography sx={{pr: 1}}>({latestBlockHeight - d.height} confirmations)</Typography>}
                    {treatAsLatest && <Typography sx={{pr: 1}}>(The latest block for the moment.)</Typography>}
                </Stack>
            </RichListItem>

            <RichListItem title={'Timestamp'} help={'The date and time at which the block was built(mined)'}>
                <Stack direction={'row'} sx={{alignItems: 'center'}} spacing={0.5}>
                    <AccessTimeIcon sx={{fontSize: 14, color: 'text.secondary'}}/>
                    <Typography>{`${formatDurationAbbr(dts, ts)} ago (${formatRFC7231(dts).substring(5)})`}</Typography>
                </Stack>
            </RichListItem>

            <RichListItem title={'Txn'} help={'The number of transactions in the block.'}>
                <Stack direction={'row'} sx={{alignItems: 'center'}} spacing={1}>
                    {/*<Button*/}
                    {/*    variant={'outlined'}*/}
                    {/*    sx={{minHeight: 16, minWidth: 120, py: 0, px: 1, fontSize: 10}}*/}
                    {/*    component={SmartLink}*/}
                    {/*    to={`/txs?block=${d.height}`}*/}
                    {/*>*/}
                    {/*`${d.txn} transactions `*/}
                    {/*</Button>*/}
                    <Typography> {`${d.txn} transactions in this block`}</Typography>
                </Stack>
            </RichListItem>

            <RichListItem title={'Coinbase Tx'}
                          help={'The first transaction in a block, and it generates new coins out of thin air'}>
                <Typography variant={'body2'} sx={{wordWrap: 'break-word'}}>
                    <SmartLink to={`/tx/0x${d.coinbaseTxId}`}>{`0x${d.coinbaseTxId}`}</SmartLink>
                </Typography>
            </RichListItem>

            <RichListItem
                title={'Block Reward'}
                help={'The total block reward received by the block producer for including the block in the blockchain, along with transfer transaction fees collected from regular transactions included in the block'}
            >
                <Typography variant={'body2'}>🏆 {d.totalfee} ABEL </Typography>
            </RichListItem>

            <RichListItem
                title={'Difficulty'}
                help={
                    'The amount of work required to mine a new block. The difficulty adjusts based on total hash power mining on the blockchain.'
                }
            >
                <Typography variant={'body2'}>{d.difficulty.toLocaleString()}</Typography>
            </RichListItem>

            <RichListItem title={'Size'} help={'The size of the block content in bytes'}>
                <Typography variant={'body2'}>{d.size.toLocaleString()} bytes</Typography>
            </RichListItem>

            <RichListItem title={'Full Size'} help={'The size of the block content and block witness in bytes'}>
                <Typography variant={'body2'}>{d.fullsize.toLocaleString()} bytes</Typography>
            </RichListItem>

            <RichListItem title={'Version'}
                          help={'The version in hex format that represents which set of block validation rules to follow.'}>
                <Typography variant={'body2'}>0x{d.versionHex}</Typography>
            </RichListItem>

            <RichListItem
                title={'Nonce'}
                help={'The number required to add blocks to the blockchain, miners can change to modify the header hash in order to produce a hash less than or equal to the target threshold.'}
            >
                <Typography variant={'body2'}>{d.nonce}</Typography>
            </RichListItem>

            <RichListItem title={'Bits'}
                          help={'An encoded version of the target threshold this block’s header hash must be less than or equal to'}>
                <Typography variant={'body2'}>{d.bits}</Typography>
            </RichListItem>

            <RichListItem
                title={'Merkle Root'}
                help={'The merkle root is derived from the hashes of all transactions included in this block, ensuring that none of those transactions can be modified without modifying the header.'}
            >
                <Typography variant={'body2'}>0x{d.merkleroot}</Typography>
            </RichListItem>

            <RichListItem title={'Hash'} help={'The hash of the block header of the current block.'}>
                <Typography variant={'body2'}>0x{d.hash}</Typography>
            </RichListItem>

            <RichListItem title={'Previous Block Hash'}
                          help={'The hash of previous block’s header. This ensures no previous block can be changed without also changing this block’s header.'}>
                <Typography variant={'body2'}>
                    <SmartLink to={`/block/0x${d.previousblockhash}`}>0x{d.previousblockhash}</SmartLink>
                </Typography>
            </RichListItem>

            <RichListItem title={'Next Block Hash'}
                          help={'The hash of next block’s header. This value would be updated when next block is appended.'}>
                <Typography variant={'body2'}>
                    {d.nextblockhash && <SmartLink to={`/block/0x${d.nextblockhash}`}>0x{d.nextblockhash}</SmartLink>}
                </Typography>
            </RichListItem>
        </RichList>
    );
};

const ViewBlock = () => {
    let {block_height_or_hash} = useParams();
    if (block_height_or_hash && block_height_or_hash.length > 20 && !block_height_or_hash.startsWith('0x')) {
        block_height_or_hash = '0x' + block_height_or_hash;
    }

    const [searchParams, setSearchParams] = useSearchParams();
    let selectedTab = Number(searchParams.get('tab') ?? 0);

    const latestBlock = useRecoilValue(latestBlockState);

    return (
        <QueryProcessor
            query={useBlockWithPrecheck(block_height_or_hash)}
            dataValidator={(data: any) => data.type === 'block'}
            renderer={(data: any) => (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography component={'span'} variant={'h2'}>
                            Block{' '}
                        </Typography>
                        <Typography component={'span'} variant={'h5'} color={'textSecondary'}>
                            #{data.payload.height}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            // variant={'outlined'}
                            sx={{minHeight: 18, minWidth: 18, p: 0, mx: 0.5}}
                            component={SmartLink}
                            to={`/block/${data.payload.height - 1}`}
                            disabled={data.payload.height === 0}
                        >
                            <Tooltip
                                title={data.payload.height === 0 ? 'No earlier block' : `View previous block (height: ${data.payload.height - 1})`}
                                arrow placement={'top'}>
                                <ArrowBackIosNewIcon sx={{fontSize: 18}}/>
                            </Tooltip>
                            <Typography>Previous Block</Typography>
                        </Button>
                    </Grid>
                    <Grid item xs={6} sx={{textAlign: 'end'}}>
                        <Button
                            // variant={'outlined'}
                            sx={{minHeight: 18, minWidth: 18, p: 0, mx: 0.5}}
                            component={SmartLink}
                            to={`/block/${data.payload.height + 1}`}
                            disabled={!latestBlock || data.payload.height >= latestBlock.height}
                        >
                            <Typography>Next Block</Typography>
                            <Tooltip
                                title={!latestBlock || data.payload.height >= latestBlock.height ? 'No later block' : `View next block (height: ${data.payload.height + 1})`}
                                arrow placement={'top'}>
                                <ArrowForwardIosIcon sx={{fontSize: 18}}/>
                            </Tooltip>
                        </Button>
                    </Grid>
                    <Grid item xs={12}>
                        <TabbedMainCard
                            labels={['Overview', 'Transactions', 'Raw Data']}
                            initialTab={selectedTab}
                            switchFn={(tabIndex: number) => {
                                const newSearchParams = new URLSearchParams(searchParams.toString());
                                newSearchParams.set('tab', tabIndex.toString())
                                setSearchParams(newSearchParams);
                            }}
                            switchForce={true}
                        >
                            <CardContent>
                                <BlockOverview d={data.payload}/>
                            </CardContent>
                            <CardContent>
                                <BlockTxList blockHeight={data.payload.height}/>
                            </CardContent>
                            <CardContent>
                                <JSONTextField value={data.payload}/>
                            </CardContent>
                        </TabbedMainCard>
                    </Grid>
                </Grid>
            )}
        />
    );
};

export default ViewBlock;
